/**
 * PERent Untermietvertrag — Formular + DIN-A4-Vorschau + getrennter PDF-Download / Druck.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Download, Printer, X } from 'lucide-react';
import { downloadHtmlAsPdf } from '../../utils/pe-papers/htmlDocumentPdfDownload';

export interface PERentTenantData {
  name: string;
  tradeName?: string;
  address: string;
  taxNumber?: string;
  vatId?: string;
  email?: string;
  phone?: string;
}

export interface PERentSalonData {
  salonName?: string;
  ownerName?: string;
  address?: string;
  taxNumber?: string;
  vatId?: string;
  iban?: string;
}

export interface PERentContractModalProps {
  isOpen: boolean;
  onClose: () => void;
  salonData?: PERentSalonData;
  tenantData: PERentTenantData;
  onUpdateTenantData: (data: Partial<PERentTenantData>) => void;
  rentNet: number;
  rentGross: number;
  monthlyHours: number;
  weeklyHours: number;
  hourlyFactor: number;
}

const OPTIONAL_EXTRAS = [
  { key: 'reception', label: 'Rezeption & Terminannahme' },
  { key: 'azubi', label: 'Azubi- & Shampooservice' },
  { key: 'edv', label: 'EDV / WLAN / Telefon' },
  { key: 'geraete', label: 'Gerätenutzung (Trockenhauben, Climazon)' },
] as const;

type ExtraKey = (typeof OPTIONAL_EXTRAS)[number]['key'];

const PRINT_ROOT_ID = 'perent-contract-print';

const PERENT_PRINT_CSS = `
  body {
    font-family: Arial, Helvetica, sans-serif;
    color: #0f172a;
    background: #ffffff;
    line-height: 1.55;
    margin: 0;
    padding: 14mm 16mm;
    font-size: 12px;
  }
  h1 {
    font-size: 18px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 4px 0 0;
  }
  h2 {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0 0 4px;
  }
  p { margin: 0 0 8px; }
  strong { font-weight: 700; }
  ul { margin: 0 0 8px; padding-left: 1.25rem; }
  header {
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid #cbd5e1;
  }
  section { margin-bottom: 14px; page-break-inside: avoid; }
  .sig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 2rem; padding-top: 2rem; }
  .sig-line { border-bottom: 2px solid #1e293b; margin-bottom: 2rem; height: 1px; }
`;

const formatMoney = (value: number) =>
  `${(Number(value) || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;

const formatHours = (value: number) =>
  `${(Number(value) || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} Std.`;

const formatDateDe = (iso: string) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE');
};

const fieldClass =
  'w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-50 placeholder:text-slate-500 focus:border-[#E6C299] focus:outline-none';

const labelClass = 'mb-1 block text-[10px] font-bold uppercase tracking-wider text-slate-400';

export const PERentContractModal: React.FC<PERentContractModalProps> = ({
  isOpen,
  onClose,
  salonData,
  tenantData,
  onUpdateTenantData,
  rentNet,
  rentGross,
  monthlyHours,
  weeklyHours,
  hourlyFactor,
}) => {
  const printContainerRef = useRef<HTMLElement>(null);
  const [isPdfDownloading, setIsPdfDownloading] = useState(false);
  const [workplaceLabel, setWorkplaceLabel] = useState(
    'Bedienstuhl 1 inkl. Mitbenutzung der Allgemeinflächen und Waschplätze'
  );
  const [termMode, setTermMode] = useState<'open' | 'fixed'>('open');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState('');
  const [noticeMonths, setNoticeMonths] = useState(3);
  const [depositEuro, setDepositEuro] = useState(0);
  const [extras, setExtras] = useState<Record<ExtraKey, boolean>>({
    reception: false,
    azubi: false,
    edv: false,
    geraete: false,
  });
  const [specialAgreements, setSpecialAgreements] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const vatAmount = Number((rentGross - rentNet).toFixed(2));
  const selectedExtras = OPTIONAL_EXTRAS.filter((e) => extras[e.key]);
  const salon = salonData || {};

  const patchTenant = (patch: Partial<PERentTenantData>) => onUpdateTenantData(patch);

  const getPrintHtml = () =>
    printContainerRef.current?.innerHTML ||
    document.getElementById(PRINT_ROOT_ID)?.innerHTML ||
    '';

  const handleDownloadPdf = async () => {
    if (isPdfDownloading) return;
    const html = getPrintHtml();
    if (!html.trim()) {
      window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
      return;
    }
    setIsPdfDownloading(true);
    try {
      const dateIso = new Date().toISOString().slice(0, 10);
      const namePart = (tenantData.name || 'Mieter')
        .trim()
        .replace(/[^\w\-äöüÄÖÜß]+/g, '_')
        .slice(0, 40);
      await downloadHtmlAsPdf({
        html,
        filename: `PERent_Untermietvertrag_${namePart}_${dateIso}.pdf`,
        scrubMode: 'minimal',
        splitPrintBreaks: true,
        extraCss: PERENT_PRINT_CSS,
      });
    } catch (err: unknown) {
      console.error('PERent PDF-Download fehlgeschlagen:', err);
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      window.alert('PDF konnte nicht heruntergeladen werden: ' + message);
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const handlePrintIsolated = () => {
    if (isPdfDownloading) return;
    const printContent = getPrintHtml();
    if (!printContent.trim()) {
      window.alert('Dokumentinhalt fehlt — bitte Vorschau prüfen.');
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const printWindow = iframe.contentWindow;
    if (!printWindow) {
      document.body.removeChild(iframe);
      return;
    }

    const doc = printWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"/><title>PERent Untermietvertrag</title>
<style>${PERENT_PRINT_CSS}</style></head><body>${printContent}</body></html>`);
    doc.close();

    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
      window.setTimeout(() => {
        if (iframe.parentNode) document.body.removeChild(iframe);
      }, 1000);
    }, 400);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100000] flex items-stretch justify-center bg-black/80 p-3 md:p-6">
      <div className="flex h-full w-full max-w-7xl flex-col overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-3">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
              PERent · Dokument
            </p>
            <h2 className="text-lg font-black uppercase tracking-wider text-[#E6C299]">
              Mietvertrag erstellen
            </h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              type="button"
              onClick={() => void handleDownloadPdf()}
              disabled={isPdfDownloading}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-b from-[#E6C299] to-[#b48a56] px-4 py-2 text-xs font-black uppercase tracking-wider text-black disabled:opacity-60"
            >
              <Download className="h-4 w-4" />
              {isPdfDownloading ? 'PDF wird erstellt…' : 'Als PDF herunterladen'}
            </button>
            <button
              type="button"
              onClick={handlePrintIsolated}
              disabled={isPdfDownloading}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E6C299]/40 bg-[#E6C299]/15 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#E6C299] disabled:opacity-60"
            >
              <Printer className="h-4 w-4" />
              Drucken
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isPdfDownloading}
              className="rounded-xl border border-slate-700 p-2 text-slate-400 hover:text-white disabled:opacity-60"
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-2">
          <div className="space-y-4 overflow-y-auto border-r border-slate-800 p-4">
            <section>
              <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-300">
                Mieterdaten
              </h3>
              <div className="space-y-3">
                <div>
                  <label className={labelClass}>Name</label>
                  <input
                    className={fieldClass}
                    value={tenantData.name}
                    onChange={(e) => patchTenant({ name: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Firmenname (optional)</label>
                  <input
                    className={fieldClass}
                    value={tenantData.tradeName || ''}
                    onChange={(e) => patchTenant({ tradeName: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>Anschrift</label>
                  <textarea
                    className={`${fieldClass} min-h-[72px]`}
                    value={tenantData.address}
                    onChange={(e) => patchTenant({ address: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>Steuernummer</label>
                    <input
                      className={fieldClass}
                      value={tenantData.taxNumber || ''}
                      onChange={(e) => patchTenant({ taxNumber: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>USt-IdNr.</label>
                    <input
                      className={fieldClass}
                      value={tenantData.vatId || ''}
                      onChange={(e) => patchTenant({ vatId: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelClass}>E-Mail</label>
                    <input
                      type="email"
                      className={fieldClass}
                      value={tenantData.email || ''}
                      onChange={(e) => patchTenant({ email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Telefon</label>
                    <input
                      className={fieldClass}
                      value={tenantData.phone || ''}
                      onChange={(e) => patchTenant({ phone: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-300">
                Mietobjekt
              </h3>
              <textarea
                className={`${fieldClass} min-h-[64px]`}
                value={workplaceLabel}
                onChange={(e) => setWorkplaceLabel(e.target.value)}
              />
            </section>

            <section>
              <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-300">
                Laufzeit
              </h3>
              <div className="mb-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => setTermMode('open')}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider ${
                    termMode === 'open'
                      ? 'bg-[#E6C299] text-black'
                      : 'border border-slate-700 text-slate-400'
                  }`}
                >
                  Unbefristet
                </button>
                <button
                  type="button"
                  onClick={() => setTermMode('fixed')}
                  className={`flex-1 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider ${
                    termMode === 'fixed'
                      ? 'bg-[#E6C299] text-black'
                      : 'border border-slate-700 text-slate-400'
                  }`}
                >
                  Befristet
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Vertragsbeginn</label>
                  <input
                    type="date"
                    className={fieldClass}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                {termMode === 'fixed' ? (
                  <div>
                    <label className={labelClass}>Enddatum</label>
                    <input
                      type="date"
                      className={fieldClass}
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                ) : (
                  <div>
                    <label className={labelClass}>Kündigungsfrist (Monate)</label>
                    <input
                      type="number"
                      min={1}
                      className={fieldClass}
                      value={noticeMonths}
                      onChange={(e) => setNoticeMonths(Math.max(1, Number(e.target.value) || 1))}
                    />
                  </div>
                )}
              </div>
              {termMode === 'fixed' && (
                <div className="mt-3">
                  <label className={labelClass}>Kündigungsfrist (Monate)</label>
                  <input
                    type="number"
                    min={1}
                    className={fieldClass}
                    value={noticeMonths}
                    onChange={(e) => setNoticeMonths(Math.max(1, Number(e.target.value) || 1))}
                  />
                </div>
              )}
            </section>

            <section>
              <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-300">
                Kaution
              </h3>
              <input
                type="number"
                min={0}
                step={0.01}
                className={fieldClass}
                value={depositEuro}
                onChange={(e) => setDepositEuro(Math.max(0, Number(e.target.value) || 0))}
              />
            </section>

            <section>
              <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-300">
                Zusatzleistungen
              </h3>
              <div className="space-y-2">
                {OPTIONAL_EXTRAS.map((item) => (
                  <label
                    key={item.key}
                    className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2 text-sm text-slate-200"
                  >
                    <input
                      type="checkbox"
                      checked={extras[item.key]}
                      onChange={() =>
                        setExtras((prev) => ({ ...prev, [item.key]: !prev[item.key] }))
                      }
                      className="accent-[#E6C299]"
                    />
                    {item.label}
                  </label>
                ))}
              </div>
            </section>

            <section>
              <h3 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-300">
                Sondervereinbarungen
              </h3>
              <textarea
                className={`${fieldClass} min-h-[96px]`}
                value={specialAgreements}
                onChange={(e) => setSpecialAgreements(e.target.value)}
                placeholder="Optional …"
              />
            </section>
          </div>

          <div className="overflow-y-auto bg-slate-800/40 p-4">
            <article
              ref={printContainerRef}
              id={PRINT_ROOT_ID}
              className="mx-auto w-full max-w-[210mm] bg-white p-8 text-xs leading-relaxed text-slate-900 shadow-xl"
            >
              <header className="mb-6 border-b border-slate-300 pb-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  PERent · Untermietvertrag (Muster)
                </p>
                <h1 className="mt-1 text-lg font-black uppercase tracking-wide text-slate-900">
                  Vertrag über Stuhl- / Platzmiete
                </h1>
              </header>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 1 Vertragspartner</h2>
                <p>
                  <strong>Vermieter:</strong> {salon.salonName || '—'}
                  {salon.ownerName ? ` (Inhaber: ${salon.ownerName})` : ''}
                  <br />
                  {salon.address || '—'}
                  <br />
                  {salon.taxNumber ? `Steuernummer: ${salon.taxNumber}` : null}
                  {salon.taxNumber && salon.vatId ? ' · ' : null}
                  {salon.vatId ? `USt-IdNr.: ${salon.vatId}` : null}
                </p>
                <p className="mt-2">
                  <strong>Mieter:</strong> {tenantData.name || '—'}
                  {tenantData.tradeName ? ` / ${tenantData.tradeName}` : ''}
                  <br />
                  {tenantData.address || '—'}
                  <br />
                  {tenantData.taxNumber ? `Steuernummer: ${tenantData.taxNumber}` : null}
                  {tenantData.taxNumber && tenantData.vatId ? ' · ' : null}
                  {tenantData.vatId ? `USt-IdNr.: ${tenantData.vatId}` : null}
                  {(tenantData.email || tenantData.phone) && (
                    <>
                      <br />
                      {[tenantData.email, tenantData.phone].filter(Boolean).join(' · ')}
                    </>
                  )}
                </p>
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 2 Mietgegenstand</h2>
                <p>
                  Der Vermieter überlässt dem Mieter zur Nutzung:{' '}
                  <strong>{workplaceLabel || '—'}</strong>.
                </p>
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">
                  § 3 Mietzeit &amp; Kapazität
                </h2>
                <p>
                  Vertragsbeginn: <strong>{formatDateDe(startDate)}</strong>
                  <br />
                  {termMode === 'fixed' ? (
                    <>
                      Befristet bis: <strong>{formatDateDe(endDate)}</strong>
                    </>
                  ) : (
                    <>Laufzeit: <strong>unbefristet / bis auf Weiteres</strong></>
                  )}
                  <br />
                  Vereinbarte Kapazität: <strong>{formatHours(weeklyHours)}</strong> / Woche
                  bzw. <strong>{formatHours(monthlyHours)}</strong> / Monat.
                </p>
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 4 Mietzins</h2>
                <p>
                  Stundenfaktor: <strong>{formatMoney(hourlyFactor)}</strong> / Std.
                  <br />
                  Monatliche Miete netto: <strong>{formatMoney(rentNet)}</strong>
                  <br />
                  zzgl. 19&nbsp;% MwSt.: <strong>{formatMoney(vatAmount)}</strong>
                  <br />
                  Monatliche Miete brutto: <strong>{formatMoney(rentGross)}</strong>
                </p>
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 5 Zusatzleistungen</h2>
                {selectedExtras.length === 0 ? (
                  <p>Es werden keine zusätzlichen Leistungen vereinbart.</p>
                ) : (
                  <ul className="list-disc pl-5">
                    {selectedExtras.map((e) => (
                      <li key={e.key}>{e.label}</li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 6 Selbstständigkeit</h2>
                <p>
                  Der Mieter handelt als selbstständige/r Unternehmer/in. Er/sie führt eine eigene
                  Kasse bzw. ein eigenes Zahlungsterminal, verkauft eigene Ware zu eigenen Preisen
                  und unterliegt keiner Weisungsgebundenheit des Vermieters hinsichtlich der
                  Ausführung der Dienstleistungen.
                </p>
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 7 Haftung &amp; Kaution</h2>
                <p>
                  {depositEuro > 0 ? (
                    <>
                      Der Mieter hinterlegt eine Kaution in Höhe von{' '}
                      <strong>{formatMoney(depositEuro)}</strong>.
                    </>
                  ) : (
                    <>Es wird keine Kaution vereinbart.</>
                  )}{' '}
                  Der Mieter haftet für von ihm verursachte Schäden am Mietgegenstand und an den
                  Einrichtungen des Salons nach den gesetzlichen Bestimmungen.
                </p>
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 8 Kündigung</h2>
                <p>
                  {termMode === 'fixed' ? (
                    <>
                      Der Vertrag endet mit Ablauf der Befristung am {formatDateDe(endDate)},
                      sofern nichts Abweichendes vereinbart ist. Eine vorzeitige ordentliche
                      Kündigung ist mit einer Frist von{' '}
                      <strong>{noticeMonths} Monaten zum Monatsende</strong> möglich, soweit
                      gesetzlich zulässig.
                    </>
                  ) : (
                    <>
                      Der Vertrag kann von beiden Seiten mit einer Frist von{' '}
                      <strong>{noticeMonths} Monaten zum Monatsende</strong> ordentlich gekündigt
                      werden. Das Recht zur außerordentlichen Kündigung aus wichtigem Grund bleibt
                      unberührt.
                    </>
                  )}
                </p>
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 9 Sondervereinbarungen</h2>
                <p className="whitespace-pre-wrap">
                  {specialAgreements.trim() || 'Keine weiteren Sondervereinbarungen.'}
                </p>
              </section>

              <section className="mb-4">
                <h2 className="mb-1 font-bold uppercase tracking-wide">§ 10 Schlussbestimmungen</h2>
                <p>
                  Sollte eine Bestimmung dieser Vereinbarung unwirksam sein, wird die Wirksamkeit der
                  übrigen Bestimmungen hiervon nicht berührt. Die Parteien verpflichten sich, die
                  unwirksame Bestimmung durch eine Vereinbarung zu ersetzen, die der unwirksamen
                  Bestimmung in Interessenlage und Bedeutung möglichst nahe kommt.
                </p>
              </section>

              <section className="mb-6">
                <h2 className="mb-3 font-bold uppercase tracking-wide">§ 11 Unterschriften</h2>
                <div className="sig-grid grid grid-cols-2 gap-8 pt-8">
                  <div>
                    <div className="sig-line mb-8 border-b border-slate-800" />
                    <p>Ort, Datum / Unterschrift Vermieter</p>
                  </div>
                  <div>
                    <div className="sig-line mb-8 border-b border-slate-800" />
                    <p>Ort, Datum / Unterschrift Mieter</p>
                  </div>
                </div>
              </section>

              <p className="border-t border-slate-200 pt-3 text-[10px] leading-snug text-slate-500">
                Rechtlicher Hinweis: Dieser Mustervertrag dient als Formulierungshilfe und ersetzt
                keine individuelle Rechts- oder Steuerberatung.
              </p>
            </article>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PERentContractModal;
